import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalContent, ModalFooter, RegularButton, Typography } from 'fronton-react';
import { useSelector } from 'react-redux';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { analyticsUtils, patchUrlByLocale } from 'utils';
import { getTitle } from 'features/page-model/ducks/get-model';
import { modalCaption, modalFooter, modalHeader } from '../styles';
import { RoundedLink } from '../rounded-link';
import { container, image } from './styles';
import { useOptimisedImages } from '@navi-toolbox/hooks';

type Props = {
  name?: string;
};

export function StoreProducts({ name }: Props) {
  const { mobile } = useBreakpoints() ?? {};
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const title = useSelector(getTitle);
  const goToCatalogue = t('goToCatalogue');
  const close = t('ui.close');
  const productsInStore = t('productsInStore');
  const storeProductsSrc =
    'https://cdn.lemanapro.ru/lmru/image/upload/v1756733864/fudzi/navigation-microfronts/store-list/store-products.jpg/store-products.jpg';

  const handleOpen = useCallback(() => {
    setOpen(true);
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: title, // название блока
      elementText: productsInStore, // текст кнопки (Как добраться транспортом, Товары в магазине, План магазина, Фото магазина)
    });
  }, [title, productsInStore]);

  const handleClose = useCallback(() => {
    setOpen(false);
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: `${title} / ${productsInStore}`, // название блока
      elementText: close, // текст кнопки (Как добраться транспортом, Товары в магазине, План магазина, Фото магазина)
    });
  }, [title, close, productsInStore]);

  const handleCatalogueNavigation = useCallback(() => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: `${title} / ${productsInStore}`, // название блока
      elementText: goToCatalogue, // текст кнопки (Как добраться транспортом, Товары в магазине, План магазина, Фото магазина)
    });
  }, [title, goToCatalogue, productsInStore]);

  const rest = {
    w: '163',
    h: '152',
  };

  const storeImageSrcOptimised = useOptimisedImages({
    src: storeProductsSrc,
    sizes: {
      rest,
    },
  });

  const optimisedImageUrl = storeImageSrcOptimised.rest?.x;

  return (
    <div>
      <RoundedLink
        title={productsInStore}
        testId="shop-items"
        className={container}
        onClick={handleOpen}
        description={t('checkProductsInStock')}
      >
        <picture>
          <source srcSet={`${storeImageSrcOptimised.rest?.xx} 2x, ${storeImageSrcOptimised.rest?.x} 1x`} />
          <img
            className={image}
            src={optimisedImageUrl}
            alt="Изображение товары в магазине"
            width={rest.w}
            height={rest.h}
            srcSet={`${storeImageSrcOptimised.rest?.xx} 2x, ${storeImageSrcOptimised.rest?.x} 1x`}
          />
        </picture>
      </RoundedLink>
      <Modal
        onClose={handleClose}
        size="s"
        variant={mobile ? 'full-screen' : 'simple'}
        data-qa="store-products-modal"
        show={open}
      >
        <div className={modalHeader}>
          <Typography color="text-secondary" variant="caption">
            {name}
          </Typography>
          <Typography className={modalCaption} variant={mobile ? 'h2' : 'h1'}>
            {productsInStore}
          </Typography>
        </div>
        <ModalContent>
          <Typography variant="m" size="body_long">
            {t('productsInStockDescription')}
          </Typography>
        </ModalContent>
        <ModalFooter className={modalFooter}>
          <RegularButton
            onClick={handleCatalogueNavigation}
            href={patchUrlByLocale('/catalogue/')}
            data-qa="to-catalogue-button"
            size="l"
            wide
            target="_blank"
            variant="secondary"
          >
            {goToCatalogue}
          </RegularButton>
        </ModalFooter>
      </Modal>
    </div>
  );
}
